import { z } from 'zod';
export { CharacterMaterialsSidecarSchema, characterMaterialsFile, assertCharacterMaterialPackage } from './CharacterMaterials';

/**
 * Avatar sidecar manifest — per-avatar variant routing and face calibration.
 *
 * Design: `_docs/Architecture/Companions/metahuman/decisions.md` (D7) and
 * `face-profile-design.md`.
 *
 * ## Why this exists
 *
 * Two gaps close with one file.
 *
 * `modelPath` is a single string on the companion descriptor and the Bevy lane hardcodes
 * `model.glb`, so nothing today can hand the compositor a different file than the browser
 * gets. The MetaHuman bake emits three variants — Draco for the room, plain for Bevy, and an
 * unpruned UE-named one for Phase 2 research — and they need routing.
 *
 * Separately, the per-avatar face profile (#3093) resolves to a built-in default because it has
 * nowhere to read from. This is that somewhere.
 *
 * Chosen over a second registry column (no migration on panda and beta) and over a derived
 * sibling filename (convention-only, and leaves the profile homeless).
 *
 * ## Compatibility is the whole design
 *
 * An avatar with NO manifest must behave exactly as it does today, and every field here is
 * optional so a manifest carrying only a `faceProfile` is valid. Omission means INHERIT, never
 * zero — these are all amplitudes, and a zeroed `gazeHorizontalMax` is a blind avatar rather
 * than a subtle miscalibration.
 *
 * Unknown keys are STRIPPED rather than rejected. A newer bake that adds a field must not brick
 * an older client into falling back to the default calibration — that failure would show up as
 * a subtly wrong face, which is far harder to notice than a missing file. The bake-time gate
 * gets the strictness instead, via `unknownFaceProfileKeys`: a typo must not silently become a
 * config value, but that is caught before publish, not at render.
 */

/**
 * The rig-geometry members of `FaceProfile`, as the wire format.
 *
 * Must stay in step with `FaceProfile` in
 * `packages/web-next/src/services/RoomManager/Agent/motion/faceChannels.ts` and its Rust mirror
 * in `compositor/compositor-service/src/companion_motion.rs`. Timing members are deliberately
 * absent and must never be added here — the alphas and `BLINK_SQUINT_THRESHOLD` are human
 * oculomotor behaviour, identical for every face, and making them per-avatar is how the Phase 2
 * gaze work gets undone one avatar at a time.
 */
export const AVATAR_FACE_PROFILE_KEYS = [
  'gazeRangeDeg',
  'gazeHorizontalMax',
  'gazeUpMax',
  'gazeDownMax',
  'gazeDivergence',
  'gazeCombinedGain',
  'blinkLookdownCoupling',
  'blinkSquintGain',
  'baselineSmile',
  'driftRange',
  'cheekCoupling',
  'eyeCoupling',
  'speakingMouthCap',
] as const;

export type AvatarFaceProfileKey = (typeof AVATAR_FACE_PROFILE_KEYS)[number];

/** Every member is a non-negative finite amplitude. `gazeRangeDeg` is degrees; the rest are morph weights. */
const amplitude = z.number().finite().nonnegative();

export const AvatarFaceProfilePatchSchema = z.object({
  gazeRangeDeg: amplitude.optional(),
  gazeHorizontalMax: amplitude.optional(),
  gazeUpMax: amplitude.optional(),
  gazeDownMax: amplitude.optional(),
  gazeDivergence: amplitude.optional(),
  gazeCombinedGain: amplitude.optional(),
  blinkLookdownCoupling: amplitude.optional(),
  blinkSquintGain: amplitude.optional(),
  baselineSmile: amplitude.optional(),
  /**
   * Emotion amplitude for this rig. Above 1 is normal and intended — the operator approved
   * 1.5 on the MetaHuman bake, so this is deliberately not capped at 1.
   */
  emotionGain: amplitude.optional(),
  driftRange: amplitude.optional(),
  cheekCoupling: amplitude.optional(),
  eyeCoupling: amplitude.optional(),
  /** How much of an emotion's smile/frown shows under the visemes while speaking (0–1). */
  speakingMouthCap: amplitude.optional(),
});

export type AvatarFaceProfilePatch = z.infer<typeof AvatarFaceProfilePatchSchema>;

/**
 * Variant filenames, resolved RELATIVE to the avatar's own asset prefix.
 *
 * Deliberately not URLs. A manifest is fetched from the CDN alongside the model, so a field
 * that accepted `https://…` or `../` would let a published asset point either renderer at an
 * arbitrary host or walk out of its own directory. Both surfaces join these onto the prefix
 * they already resolved, and nothing else.
 */
const variantPath = z
  .string()
  .min(1)
  .refine((v) => !v.includes('..'), { message: 'must not traverse parent directories' })
  .refine((v) => !v.startsWith('/') && !v.startsWith('\\'), { message: 'must be relative' })
  .refine((v) => !/^[a-z][a-z0-9+.-]*:/i.test(v), { message: 'must not carry a URL scheme' });

export const AvatarVariantsSchema = z.object({
  /** The room / R3F asset. Draco-compressed for MetaHuman bakes. */
  room: variantPath.optional(),
  /** The Bevy asset. Must be non-Draco — the compositor has no mesh decoder. */
  compositor: variantPath.optional(),
  /** Unpruned, UE-named. The Phase 2 RigLogic asset; not contract-conforming by design. */
  research: variantPath.optional(),
  /**
   * The room model at small on-screen sizes (#4863, Hold'em seats): the same meshes and morph
   * targets with morph normals dropped and textures capped at 1k. Shares the room's sidecar.
   */
  seat: variantPath.optional(),
});

export type AvatarVariants = z.infer<typeof AvatarVariantsSchema>;

/**
 * Morph targets to DROP when building the compositor variant.
 *
 * Bevy caps a mesh at 64 morph targets (`MAX_MORPH_TARGETS` in
 * `companion_bevy.rs`). A contract-complete MetaHuman carries 72, so eight
 * must go — and this is a hard renderer limit, not a size budget, so D8
 * ("fidelity beats size") does not soften it. The browser is unaffected: the
 * incumbent Avaturn model already ships 72 and renders fine.
 *
 * Declared here so the choice is made once, per avatar, by someone looking at
 * the character — rather than left to `conform_model_glb`, which prunes at
 * load using a global `MORPH_DROP_PRIORITY`. That default currently sacrifices
 * `tongueOut`, `cheekPuff`, `noseSneer*` and `eyeWide*`: precisely the
 * channels nothing drives today and which are therefore the cheapest
 * expressive gain available for conversational reactions — surprise,
 * skepticism. Dropping them silently is the wrong default once they are used.
 *
 * Two entries are defensible on evidence rather than taste. `faceChannels.ts`
 * describes the combined `eyesLookUp`/`eyesLookDown` as "a coarser duplicate
 * of the per-eye pair", superseded by the eight per-eye channels; and
 * `tongueOut` is driven by nothing on either surface. The rest is a product
 * judgement between lipsync fidelity and reaction range, which Stage A's A/B
 * and the gauntlet bar exist to settle.
 *
 * Omitted means "let the runtime decide" — i.e. today's behaviour. An empty
 * array is different, and means "drop nothing", which will overflow the cap
 * on a 72-morph model and let the runtime prune anyway.
 */
export const CompositorMorphDropsSchema = z.array(z.string().min(1)).max(64);

export type CompositorMorphDrops = z.infer<typeof CompositorMorphDropsSchema>;

/**
 * Pre-rendered seat loops (#4863, multi-companion Phase 5): the character listening and
 * speaking, as sprite sheets beside the model, for seats a device cannot give a live head.
 * Frames run left to right, top to bottom, `cols` to a row, each `frameWidth` x `frameHeight`.
 * Captured by `scripts/media/capture-companion-loops.mjs`; shipped inside the version.
 */
export const SeatLoopsSchema = z.object({
  fps: z.number().positive().max(30),
  frames: z.number().int().min(1).max(120),
  cols: z.number().int().min(1).max(32),
  rows: z.number().int().min(1).max(32),
  frameWidth: z.number().int().min(16).max(1024),
  frameHeight: z.number().int().min(16).max(1024),
  files: z.object({
    listening: variantPath,
    speaking: variantPath.optional(),
  }),
});

export type SeatLoops = z.infer<typeof SeatLoopsSchema>;

export const AvatarManifestSchema = z.object({
  variants: AvatarVariantsSchema.optional(),
  loops: SeatLoopsSchema.optional(),
  faceProfile: AvatarFaceProfilePatchSchema.optional(),
  /** Morphs to omit from the compositor variant so it fits Bevy's 64 cap. */
  compositorMorphDrops: CompositorMorphDropsSchema.optional(),
});

export type AvatarManifest = z.infer<typeof AvatarManifestSchema>;

/** Immutable package settings. Preview paths always name files inside the
 * version so activation, promotion and rollback carry their bytes together. */
const releaseAssetPath = z.string().regex(/^[A-Za-z0-9_./-]+$/)
  .refine(path => !path.split('/').some(part => !part || part === '.' || part === '..'));
export const CharacterVersionFileSchema = z.object({
  path: releaseAssetPath, bytes: z.number().int().nonnegative(), sha256: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
export const CharacterReleaseMetadataSchema = z.object({
  previews: z.object({ video: releaseAssetPath.nullable().optional(), image: releaseAssetPath.nullable().optional(), thumbnail: releaseAssetPath.nullable().optional() }).strict().optional(),
  defaultVoice: z.object({ id: z.string().trim().min(1), provider: z.string().trim().min(1) }).strict().nullable().optional(),
  tuningId: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/).nullable().optional(),
  wardrobe: z.record(z.unknown()).optional(),
  framing: z.object({ fov: z.number().finite().positive().lt(180), heightOffset: z.number().finite() }).strict().nullable().optional(),
}).passthrough();
export type CharacterReleaseMetadata = z.infer<typeof CharacterReleaseMetadataSchema>;

/** Publisher-owned identity in character.json. The slug is permanent; names
 * may change without changing room assignments or a release's rendering. */
export const CharacterPackageIdentitySchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/),
  name: z.string().trim().min(1).max(255),
  gender: z.string().trim().min(1).max(50).nullable().default(null),
  tags: z.array(z.string().trim().min(1)).max(100).default([]),
});
export type CharacterPackageIdentity = z.infer<typeof CharacterPackageIdentitySchema>;

export const CharacterRuntimeReleaseSchema = z.object({
  version: z.string(), build: z.string(), characterId: z.string().nullable(),
  modelPath: z.string(), manifestPath: z.string().nullable(),
  /** Null declares a generic GLB. Omitted only by servers predating this binding. */
  materialsPath: z.string().nullable().optional(),
  previews: z.object({ video: z.string().nullable(), image: z.string().nullable(), thumbnail: z.string().nullable() }),
  defaultVoice: z.object({ id: z.string(), provider: z.string() }).nullable(),
  tuningId: z.string().nullable(), wardrobe: z.record(z.unknown()),
  framing: z.object({ fov: z.number(), heightOffset: z.number() }).nullable(),
});
export type CharacterRuntimeRelease = z.infer<typeof CharacterRuntimeReleaseSchema>;

/** Apply CDN/proxy URL formatting without separating settings from their model. */
export function mapCharacterReleaseAssets(
  release: CharacterRuntimeRelease | null | undefined,
  format: (path: string) => string,
): CharacterRuntimeRelease | null {
  if (!release) return null;
  const asset = (path: string | null) => path ? format(path) : null;
  return { ...release, modelPath: format(release.modelPath), manifestPath: asset(release.manifestPath),
    ...(release.materialsPath !== undefined ? { materialsPath: asset(release.materialsPath) } : {}),
    previews: { video: asset(release.previews.video), image: asset(release.previews.image), thumbnail: asset(release.previews.thumbnail) } };
}


/** What both surfaces fall back to when no manifest exists — today's behaviour, exactly. */
export const DEFAULT_MODEL_FILE_NAME = 'model.glb';

/**
 * Parse a fetched manifest, or `null` if it is unusable.
 *
 * Never throws. A malformed manifest must degrade to the built-in defaults rather than take a
 * companion down — the avatar still renders, just on the default calibration.
 */
export function parseAvatarManifest(raw: unknown): AvatarManifest | null {
  const result = AvatarManifestSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * Which variant file a surface should load.
 *
 * `compositor` and `research` fall back to the room variant, which in turn falls back to
 * `model.glb`. That fallback is what preserves current behaviour for every existing avatar:
 * the compositor reads the same file it reads today.
 *
 * Note the consequence for MetaHuman specifically — if a bake publishes a Draco room variant
 * and omits `compositor`, the compositor inherits an asset it cannot decode. That is caught by
 * the `uses_draco` preflight gate rather than papered over here, because silently substituting
 * a different file would hide a broken bake.
 */
export function resolveVariantPath(
  manifest: AvatarManifest | null | undefined,
  kind: keyof AvatarVariants,
): string {
  const variants = manifest?.variants;
  return variants?.[kind] ?? variants?.room ?? DEFAULT_MODEL_FILE_NAME;
}

/**
 * Face-profile keys present in the raw input that this build does not recognise.
 *
 * For the bake-time acceptance gate, NOT for the runtime path. Runtime strips unknown keys so a
 * newer manifest cannot brick an older client; the gate is where a typo gets caught, before an
 * asset is published with a calibration field that silently does nothing.
 */
/** Bevy's per-mesh morph target ceiling (`companion_bevy.rs`). */
export const COMPOSITOR_MAX_MORPH_TARGETS = 64;

/**
 * How many more morphs must be dropped for `morphCount` to fit the cap.
 *
 * For the bake-time gate: a manifest declaring too few drops means the runtime
 * silently prunes the remainder, which is exactly what declaring them was
 * meant to prevent. Returns 0 when the declaration is sufficient.
 */
export function compositorMorphShortfall(
  morphCount: number,
  drops: CompositorMorphDrops | undefined,
): number {
  const declared = drops?.length ?? 0;
  return Math.max(0, morphCount - declared - COMPOSITOR_MAX_MORPH_TARGETS);
}

export function unknownFaceProfileKeys(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const profile = (raw as { faceProfile?: unknown }).faceProfile;
  if (!profile || typeof profile !== 'object') return [];
  const known = new Set<string>(AVATAR_FACE_PROFILE_KEYS);
  return Object.keys(profile as Record<string, unknown>).filter((k) => !known.has(k));
}
